import { css } from "@emotion/css";
import { Callout, ExternalLink, Note } from "@octopusdeploy/design-system-components";
import { ScriptingLanguage } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { useState } from "react";
import { contentContainerStyles } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/Styles";
import { CodeEditor, type Language, type TextFormat } from "~/components/CodeEditor/CodeEditor";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
type MonitorUpgradePageProps = {
    generateHelmCommand: (selectedLanguage: ScriptingLanguage | Language | TextFormat) => string;
    foundAgentDetails: boolean;
    accessTokenExpired: boolean;
    accessTokenExpiry: moment.Moment | undefined;
};
export function KubernetesMonitorUpgradePage({ generateHelmCommand, foundAgentDetails, accessTokenExpired, accessTokenExpiry }: MonitorUpgradePageProps) {
    const [selectedLanguage, setSelectedLanguage] = useState<ScriptingLanguage | Language | TextFormat>(ScriptingLanguage.Bash);
    // This happens when the agent is unavailable
    if (!foundAgentDetails) {
        return (<Callout title={"Could not determine agent status"} type={"danger"}>
                We could not determine the status of the Kubernetes agent. Please check that the agent is running and reporting as healthy.
            </Callout>);
    }
    const expiryTimeInfo = accessTokenExpiry && (<>
            Your token expires{" "}
            <b>
                <TimeFromNowLabel time={accessTokenExpiry.toISOString()}/>
            </b>
            .
        </>);
    const helmCommand = generateHelmCommand(selectedLanguage);
    const helmCommandLineCount = helmCommand.split(/\r\n|\r|\n/).length;
    //adjust the height of the code editor based on the number of lines in the helm command
    const codeEditorContainer = css({
        // 1.25rem per line (line height) and roughly 3rem of padding/margins/scrollbars
        minHeight: `calc((${helmCommandLineCount} * 1.25rem) + 3rem)`,
    });
    return (<div className={contentContainerStyles}>
            {!accessTokenExpired && (<>
                    <p>We've generated a custom Helm command for you which includes a short-lived bearer token for Octopus Server. {expiryTimeInfo}</p>
                    <p>Copy and run this Helm command in a terminal connected to your Kubernetes cluster.</p>
                </>)}
            {accessTokenExpired && (<Callout title={"Your bearer token has expired"} type={"danger"}>
                    <p>Regenerate the token and copy the updated Helm command.</p>
                </Callout>)}
            <>
                <CodeEditor value={helmCommand} readOnly lineWrapping={false} showCopyButton showLineNumbers={false} language={selectedLanguage} allowFullScreen containerClassName={codeEditorContainer} settingsOverride={{ theme: "default", wordWrap: false }} onChange={noOp} scriptingLanguageSelectorOptions={{
            supportedLanguages: [ScriptingLanguage.Bash, ScriptingLanguage.PowerShell],
            onScriptingLanguageChanged: (syntax: ScriptingLanguage | Language | TextFormat) => setSelectedLanguage(syntax),
        }}/>
                <Note>
                    Use of the Kubernetes monitor is subject to our <ExternalLink href={"https://octopus.com/company/legal"}>Customer Agreement</ExternalLink> with you.
                </Note>
                <Note>Once you've run the command, run a health check on your agent to update the detected configuration.</Note>
            </>
        </div>);
}
